import { DOCUMENT } from '@angular/common';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, DestroyRef, Inject, OnDestroy, ViewChild } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { TranslatePipe } from '@ngx-translate/core';
import { Observable } from 'rxjs';
import { ClientDTO } from 'src/app/base/models/models.swagger.generated';
import { AuthService } from 'src/app/base/services/auth.service';
import { ThemeMode, TenantThemePayload } from 'src/app/base/services/theme.model';
import { TenantThemeService } from 'src/app/base/services/tenant-theme.service';
import { ThemeService } from 'src/app/base/services/theme.service';
import { ThemeModeSwitcherComponent } from './theme-mode-switcher/theme-mode-switcher.component';
import { TenantThemeEditorComponent } from './tenant-theme-editor/tenant-theme-editor.component';
import { BootstrapShowcaseComponent } from './bootstrap-showcase/bootstrap-showcase.component';
import { SyncfusionShowcaseComponent } from './syncfusion-showcase/syncfusion-showcase.component';
import { ValidationDemoComponent } from './validation-demo/validation-demo.component';
import { ThemeGridShowcaseComponent } from './theme-grid-showcase/theme-grid-showcase.component';
import { ThemeLogoPreviewComponent } from './theme-logo-preview/theme-logo-preview.component';
import { ThemeClientSwitcherComponent } from './theme-client-switcher/theme-client-switcher.component';
import { CollapseButtonComponent } from 'src/app/base/shared/collapse-button/collapse-button.component';
import { ClientService } from 'src/app/base/services/client/client.service';
import { CanComponentDeactivate } from 'src/app/base/guards/unsaved-changes.guard';
import { buildOverridesFromSnapshot, captureThemeTokenPreviewSnapshot } from './utils/tenant-theme-snapshot.util';

@Component({
  selector: 'app-theme-showcase',
  templateUrl: './theme-showcase.component.html',
  styleUrls: ['./theme-showcase.component.scss'],
  imports: [
    TranslatePipe,
    ThemeModeSwitcherComponent,
    TenantThemeEditorComponent,
    BootstrapShowcaseComponent,
    SyncfusionShowcaseComponent,
    ValidationDemoComponent,
    ThemeGridShowcaseComponent,
    ThemeLogoPreviewComponent,
    ThemeClientSwitcherComponent,
    CollapseButtonComponent,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class ThemeShowcaseComponent implements OnDestroy, CanComponentDeactivate {
  @ViewChild(TenantThemeEditorComponent) public tenantThemeEditor?: TenantThemeEditorComponent;
  @ViewChild(SyncfusionShowcaseComponent) public syncfusionShowcase?: SyncfusionShowcaseComponent;
  @ViewChild(BootstrapShowcaseComponent) public bootstrapShowcase?: BootstrapShowcaseComponent;

  public readonly currentThemeMode$: Observable<ThemeMode>;

  public selectedClient: ClientDTO | null = null;
  public isLoadingClientTheme = false;
  public isSavingTenantTheme = false;
  public isClientThemeDirty = false;
  public isGridSectionExpanded = true;
  public isLogoSectionExpanded = true;
  public isComponentsSectionExpanded = true;

  public get canChooseClient(): boolean {
    return this.authService.isUserAdmin() && !this.authService.hasUserClientAssigned();
  }

  private readonly originalTenantId: string | null;

  constructor(
    @Inject(DOCUMENT) private readonly document: Document,
    private readonly themeService: ThemeService,
    private readonly tenantThemeService: TenantThemeService,
    private readonly authService: AuthService,
    private readonly destroyRef: DestroyRef,
    private readonly clientService: ClientService,
    private readonly changeDetector: ChangeDetectorRef
  ) {
    this.currentThemeMode$ = this.themeService.mode$;
    this.originalTenantId = this.themeService.getThemeState().tenantId ?? null;
    this.initializeSelectedClient();
  }

  public ngOnDestroy(): void {
    this.restoreOriginalTenantTheme();
  }

  public hasUnsavedChanges(): boolean {
    return this.isClientThemeDirty;
  }

  public setTheme(mode: ThemeMode): void {
    this.themeService.setMode(mode);
  }

  public onSelectedClientChange(client: ClientDTO | null): void {
    this.selectedClient = client;
    this.loadClientTheme(client);
  }

  public onDirtyChange(isDirty: boolean): void {
    this.isClientThemeDirty = isDirty;
  }

  public onSave(payload: TenantThemePayload): void {
    this.isSavingTenantTheme = true;
    this.tenantThemeService.saveForTenant(this.selectedClient?.id ?? null, payload)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (savedPayload: TenantThemePayload | null) => {
          this.isSavingTenantTheme = false;
          this.tenantThemeEditor?.onSaved(savedPayload);
          this.changeDetector.markForCheck();
        },
        error: () => {
          this.isSavingTenantTheme = false;
          this.changeDetector.markForCheck();
        }
      });
  }

  public onValidate(): void {
    this.syncfusionShowcase?.triggerValidationDemo();
    this.bootstrapShowcase?.triggerValidationDemo();
  }

  public onResetValidation(): void {
    this.syncfusionShowcase?.resetValidationDemo();
    this.bootstrapShowcase?.resetValidationDemo();
  }

  public toggleGridSection(): void {
    this.isGridSectionExpanded = !this.isGridSectionExpanded;
  }

  public toggleLogoSection(): void {
    this.isLogoSectionExpanded = !this.isLogoSectionExpanded;
  }

  public toggleComponentsSection(): void {
    this.isComponentsSectionExpanded = !this.isComponentsSectionExpanded;
  }

  public get tenantLogoPreviewUrl(): string {
    return this.tenantThemeEditor?.tenantLogoPreviewUrl ?? this.themeService.getDefaultLogoUrl();
  }

  public get hasTenantLogoUrl(): boolean {
    return this.tenantThemeEditor?.hasTenantLogoUrl ?? false;
  }

  private loadClientTheme(client: ClientDTO | null): void {
    const tenantId = client?.id ?? null;
    if (!tenantId) {
      this.tenantThemeEditor?.applyLoadedTheme(null);
      return;
    }

    this.isLoadingClientTheme = true;
    this.tenantThemeService.applyForTenant(tenantId)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => {
          this.isLoadingClientTheme = false;
          this.tenantThemeEditor?.applyLoadedTheme(this.readCurrentTenantThemePayload());
          this.changeDetector.markForCheck();
        },
        error: () => {
          this.isLoadingClientTheme = false;
          this.changeDetector.markForCheck();
        }
      });
  }

  private readCurrentTenantThemePayload(): TenantThemePayload | null {
    const rootElement = this.document.documentElement;
    const primary = rootElement.style.getPropertyValue('--tenant-primary').trim();
    const secondary = rootElement.style.getPropertyValue('--tenant-secondary').trim();
    const logoUrl = this.themeService.getCurrentLogoUrl() ?? '';
    const logoUrlDarkMode = this.themeService.getCurrentLogoUrlDarkMode() ?? '';

    const overrides = buildOverridesFromSnapshot(captureThemeTokenPreviewSnapshot(this.document));
    const hasOverrides = Boolean(
      Object.keys(overrides.light ?? {}).length ||
      Object.keys(overrides.dark ?? {}).length
    );

    const hasAnyValue = Boolean(primary || secondary || logoUrl || logoUrlDarkMode || hasOverrides);
    return hasAnyValue ? { primary, secondary, logoUrl, logoUrlDarkMode, overrides } : null;
  }

  private initializeSelectedClient(): void {
    if (this.authService.hasUserClientAssigned()) {
      this.selectedClient = this.authService.getUserInfo()?.client ?? null;
      this.loadClientTheme(this.selectedClient);
      return;
    }

    if (this.authService.isUserAdmin() && this.clientService.selectedClient) {
      this.selectedClient = this.clientService.selectedClient;
      this.loadClientTheme(this.selectedClient);
    }
  }

  private restoreOriginalTenantTheme(): void {
    if (this.authService.hasUserClientAssigned()) {
      return;
    }

    if (this.originalTenantId) {
      this.tenantThemeService.applyForTenant(this.originalTenantId)
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe();
    } else {
      this.themeService.applyTenantTheme('default', null);
    }
  }
}
